import React, { createContext, useContext, useRef, useState, useEffect } from 'react';
import { cn } from '@/utils/cn';
// Context for managing dropdown state
interface DropdownMenuContextValue {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const DropdownMenuContext = createContext<DropdownMenuContextValue | null>(null);

// Main dropdown container
export interface DropdownMenuProps {
  children: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({ children, onOpenChange }) => {
  const [_, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(event.target as Node)) {
        const dropdownContent = document.querySelector('[data-dropdown-content]');
        if (dropdownContent && !dropdownContent.contains(event.target as Node)) {
          handleOpenChange(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <DropdownMenuContext.Provider value={{ open, onOpenChange: handleOpenChange, triggerRef }}>
      <div className="relative inline-block text-left">{children}</div>
    </DropdownMenuContext.Provider>
  );
};

// Dropdown trigger button
export interface DropdownMenuTriggerProps {
  children: React.ReactNode;
  className?: string;
  asChild?: boolean;
}

export const DropdownMenuTrigger = React.forwardRef<HTMLButtonElement, DropdownMenuTriggerProps>(
  ({ children, className, asChild, ...props }, _ref) => {
    const context = useContext(DropdownMenuContext);

    if (!context) {
      throw new Error('DropdownMenuTrigger must be used within a DropdownMenu');
    }

    const handleClick = () => {
      context.onOpenChange(!context.open);
    };

    if (asChild && React.isValidElement(children)) {
      return React.cloneElement(children, {
        ...children.props,
        ref: context.triggerRef,
        onClick: handleClick,
        'aria-expanded': context.open,
        'aria-haspopup': 'menu',
      });
    }

    return (
      <button
        ref={context.triggerRef}
        onClick={handleClick}
        aria-expanded={context.open}
        aria-haspopup="menu"
        className={cn(
          'inline-flex items-center justify-center rounded-md px-3 py-2 text-sm font-medium',
          'ring-offset-white transition-colors focus-visible:outline-none focus-visible:ring-2',
          'focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          'hover:bg-gray-100 hover:text-gray-900',
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

DropdownMenuTrigger.displayName = 'DropdownMenuTrigger';

// Dropdown content container
export interface DropdownMenuContentProps {
  children: React.ReactNode;
  className?: string;
  align?: 'start' | 'center' | 'end';
  side?: 'top' | 'bottom' | 'left' | 'right';
}

export const DropdownMenuContent = React.forwardRef<HTMLDivElement, DropdownMenuContentProps>(
  ({ children, className, align = 'center', side = 'bottom', ...props }, ref) => {
    const context = useContext(DropdownMenuContext);

    if (!context) {
      throw new Error('DropdownMenuContent must be used within a DropdownMenu');
    }

    if (!context.open) return null;

    const alignmentClasses = {
      start: 'left-0',
      center: 'left-1/2 transform -translate-x-1/2',
      end: 'right-0',
    };

    const sideClasses = {
      top: 'bottom-full mb-1',
      bottom: 'top-full mt-1',
      left: 'right-full mr-1 top-0',
      right: 'left-full ml-1 top-0',
    };

    return (
      <div
        ref={ref}
        data-dropdown-content
        role="menu"
        className={cn(
          'absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-white p-1 shadow-lg',
          'animate-in fade-in-0 zoom-in-95 data-[side=bottom]:slide-in-from-top-2',
          'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
          'data-[side=top]:slide-in-from-bottom-2',
          alignmentClasses[align],
          sideClasses[side],
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

DropdownMenuContent.displayName = 'DropdownMenuContent';

// Individual menu item
export interface DropdownMenuItemProps {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: () => void;
}

export const DropdownMenuItem = React.forwardRef<HTMLDivElement, DropdownMenuItemProps>(
  ({ children, className, disabled, onClick, ...props }, ref) => {
    const context = useContext(DropdownMenuContext);

    const handleClick = () => {
      if (disabled) return;
      onClick?.();
      context?.onOpenChange(false);
    };

    return (
      <div
        ref={ref}
        role="menuitem"
        onClick={handleClick}
        className={cn(
          'relative flex cursor-default select-none items-center rounded-sm px-2 py-1.5 text-sm',
          'outline-none transition-colors focus:bg-gray-100 focus:text-gray-900',
          disabled
            ? 'pointer-events-none opacity-50'
            : 'hover:bg-gray-100 hover:text-gray-900 cursor-pointer',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

DropdownMenuItem.displayName = 'DropdownMenuItem';

// Menu separator
export interface DropdownMenuSeparatorProps {
  className?: string;
}

export const DropdownMenuSeparator = React.forwardRef<HTMLDivElement, DropdownMenuSeparatorProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        role="separator"
        className={cn('-mx-1 my-1 h-px bg-gray-200', className)}
        {...props}
      />
    );
  }
);

DropdownMenuSeparator.displayName = 'DropdownMenuSeparator';
